import React, { useState, useRef, useEffect } from "react";
import ISO6391 from "iso-639-1";
import speech from '../assests/volume.png';
import no_speech from '../assests/silent.png';
import { voiceStore } from "../stores/voiceStore";
import { observer } from "mobx-react-lite";
import { useLocation, useParams } from "react-router-dom";
import { saveLogEvent } from "../utils/Utils";

// Debounce Hook
function useDebouncedEffect(callback, dependencies, delay) {
    const timeoutRef = useRef(null);

    React.useEffect(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            callback();
        }, delay);

        return () => clearTimeout(timeoutRef.current);
    }, dependencies);
}

const TextToSpeech = () => {
    const getInitialLanguage = () => {
        const lan = sessionStorage.getItem("CommunicationLanguage");
        if (!lan) return "en";
        const cleanLan = lan.trim().split('"').join('');
        return ISO6391.getCode(cleanLan) || "en";
    };

    const escapeSpecialCharacters = (text) => text.replace(/'/g, "");

    const cleanTranslationText = (text) => {
        const parser = new DOMParser();
        const decodedText = parser.parseFromString(`<!doctype html><body>${text}`, 'text/html').body.textContent;
        return decodeURIComponent(decodedText.replace(/&#(\d+);/g, (match, dec) => String.fromCharCode(dec)));
    };

    const [language, setLanguage] = useState(getInitialLanguage());
    const [audioUrl, setAudioUrl] = useState("");
    const content = voiceStore.voiceCommand;

    const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

    const location = useLocation();
    const { customerNumber, groupNumber } = useParams();

    // Map route to descriptive page name
    const getPageName = () => {
        // Handle dynamic routes for payment
        if (location.pathname.startsWith("/payment")) {
            if (groupNumber) {
                return "PaymentPage - Group Pay dues page";
            } else if (customerNumber) {
                return "PaymentPage - Pay Own Dues";
            }
            return "Payment Page";
        }
        if (location.pathname.startsWith("/multiplePayment")) {
            return "PaymentPage - Payment for selected customer";
        }
        if (location.pathname === "/customerDetails") {
            return "Group Details Page";
        }
        if (location.pathname === "/login") {
            return "Login Page";
        }
        if (location.pathname === "/contact-us") {
            return "Contact Page";
        }
        if (location.pathname === "/soa") {
            return "Statement of Account Page";
        }
        if (location.pathname === "/") {
            return "Home Page";
        }
        if (location.pathname === "*" || location.pathname === "/error") {
            return "Error Page";
        }
        return location.pathname;
    };

    const handleTranslateAndSpeak = async () => {
        if (!content) return;

        try {
            const escapedContent = escapeSpecialCharacters(content);
            const translationResponse = await fetch(
                "https://translation.googleapis.com/language/translate/v2",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-Goog-Api-Key": API_KEY
                    },
                    body: JSON.stringify({ q: escapedContent, target: language })
                }
            );

            if (!translationResponse.ok) throw new Error("Translation API failed");

            const translationData = await translationResponse.json();
            const cleanedText = cleanTranslationText(translationData.data.translations[0].translatedText);

            const speechResponse = await fetch(
                "https://texttospeech.googleapis.com/v1/text:synthesize",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-Goog-Api-Key": API_KEY
                    },
                    body: JSON.stringify({
                        input: { text: cleanedText },
                        voice: { languageCode: language, ssmlGender: "NEUTRAL" },
                        audioConfig: { audioEncoding: "MP3" },
                    }),
                }
            );

            if (!speechResponse.ok) throw new Error("Text-to-Speech API failed");

            const speechData = await speechResponse.json();
            setAudioUrl(`data:audio/mp3;base64,${speechData.audioContent}`);
        } catch (error) {
            console.error("Error:", error);
            alert("An error occurred. Please check the console for details.");
        }
    };

    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);

    const handlePlayPause = async () => {
        // Log the audio button click event with descriptive page name
        await saveLogEvent({
            logLevel: "info",
            statusCode: 200,
            status: "success",
            message: {
                trigger: "Audio button clicked",
                Page: getPageName(),
            },
            payload: {
                trigger: "Audio button clicked",
                Page: getPageName(),
            },
        });
        if (audioRef.current.paused) {
            audioRef.current.play();
            setIsPlaying(true);
        } else {
            audioRef.current.pause();
            setIsPlaying(false);
        }
    };

    const handleTrackEnd = () => setIsPlaying(false);

    // Debounce added here to prevent duplicate requests
    useDebouncedEffect(() => {
        if (content) {
            handleTranslateAndSpeak();
        }
    }, [language, content], 300);

    useEffect(() => {
        setLanguage(getInitialLanguage())
    }, [language, voiceStore.language])

    return (
        <>
            <audio ref={audioRef} src={audioUrl} onEnded={handleTrackEnd}></audio>
            <div className="absolute top-2 right-2 z-10">
                <button
                    className="p-3 bg-red rounded-full cursor-pointer border border-black shadow-lg"
                    onClick={handlePlayPause}
                    disabled={!content}
                >

                    <img
                        src={isPlaying ? no_speech : speech}
                        alt={isPlaying ? "No Speech" : "Speech"}
                        className="w-5 h-5"
                    />
                </button>
            </div>
        </>
    );
}

export default observer(TextToSpeech);